/**
 * useMobileDetect Hook (Jan 15, 2026)
 * 
 * Detects if user is on a mobile device using multiple methods:
 * 1. Viewport width (< 768px)
 * 2. User agent string
 * 3. Touch capability
 * 
 * Returns object with device info and responsive breakpoints
 */
import { useState, useEffect } from 'react';

const useMobileDetect = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isTouchDevice: false,
    screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
    orientation: 'landscape'
  });

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Check user agent for mobile devices
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
      const tabletRegex = /iPad|Android(?!.*Mobile)|Tablet/i;
      
      const isMobileUA = mobileRegex.test(userAgent);
      const isTabletUA = tabletRegex.test(userAgent);
      
      // Check touch capability
      const isTouchDevice = 'ontouchstart' in window || 
        navigator.maxTouchPoints > 0 || 
        navigator.msMaxTouchPoints > 0;
      
      // Determine device type based on width and user agent
      // Mobile: < 768px or mobile UA
      // Tablet: 768px - 1024px or tablet UA
      // Desktop: > 1024px and not mobile/tablet UA
      const isMobile = width < 768 || (isMobileUA && !isTabletUA);
      const isTablet = (width >= 768 && width < 1024) || isTabletUA;
      const isDesktop = width >= 1024 && !isMobileUA && !isTabletUA;
      
      // Determine orientation
      const orientation = width > height ? 'landscape' : 'portrait';
      
      setDeviceInfo({
        isMobile,
        isTablet,
        isDesktop,
        isTouchDevice,
        screenWidth: width,
        orientation
      });
    };

    // Initial check
    checkDevice();

    // Listen for resize events
    window.addEventListener('resize', checkDevice);
    
    // Listen for orientation changes
    window.addEventListener('orientationchange', () => {
      // Delay check to allow orientation change to complete
      setTimeout(checkDevice, 100);
    });

    return () => {
      window.removeEventListener('resize', checkDevice);
      window.removeEventListener('orientationchange', checkDevice);
    };
  }, []);

  return deviceInfo;
};

export default useMobileDetect;
